import { isZeroOrBlank } from "@compliance/domain";
import {
  Card,
  Group,
  Loader,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconCalculator,
  IconCoin,
  IconFlame,
  IconGauge,
  IconRouteSquare,
} from "@tabler/icons-react";
import { useBatchFigures } from "../../api/hooks";
import type { BatchDetail, Figure } from "../../api/types";
import { UnitValue } from "../../components/UnitValue";
import { RecomputeButton } from "./RecomputeButton";

export function Workpaper({ batch }: { batch: BatchDetail }) {
  // Shared with the Batches dashboard — the single home for the calc-kind →
  // figure mapping (prefers the inline `totals` envelope, else calc-results).
  const { isPending, totalHubMscf, tokenMmbtu, utilityMmbtu, eligibleMmbtu, rfGallons, d3Rins } =
    useBatchFigures(batch);

  const allFigures = [totalHubMscf, tokenMmbtu, utilityMmbtu, eligibleMmbtu, rfGallons, d3Rins];
  const empty = isPending ? false : allFigures.every((f) => !f || isZeroOrBlank(f.value));

  const periodLabel = batch.period_label ?? batch.period_start.slice(0, 7);

  return (
    <Stack gap="lg">
      {/* The figures below are what a recompute rewrites, so the control lives
          here rather than on a lifecycle menu somewhere else. State travels
          with the id because a locked batch refuses the recompute server-side. */}
      <RecomputeButton batchId={batch.batch_id} batchState={batch.state} />
      {isPending ? (
        <Card>
          <Stack align="center" gap="sm" py="lg">
            <Loader size="md" />
            <Text c="dimmed" size="sm">
              Loading calculated figures…
            </Text>
          </Stack>
        </Card>
      ) : empty ? (
        <Card>
          <Stack gap="sm" align="center" py="lg">
            <ThemeIcon variant="light" color="gray" size={40} radius="xl">
              <IconCalculator size={20} />
            </ThemeIcon>
            <Text c="dimmed" size="sm" ta="center">
              The {periodLabel} batch hasn't been calculated yet — figures populate once the calc
              engine has run for the period.
            </Text>
          </Stack>
        </Card>
      ) : (
        <Stack gap="xs">
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            {totalHubMscf ? (
              <KpiTile
                icon={<IconFlame size={18} />}
                color="indigo"
                label="Total HUB MSCF"
                figure={totalHubMscf}
              />
            ) : null}
            {tokenMmbtu ? (
              <KpiTile
                icon={<IconCalculator size={18} />}
                color="teal"
                label="Token MMBtu HHV"
                figure={tokenMmbtu}
              />
            ) : null}
            {utilityMmbtu ? (
              <KpiTile
                icon={<IconRouteSquare size={18} />}
                color="grape"
                label="Utility MMBtu HHV"
                figure={utilityMmbtu}
              />
            ) : null}
            {eligibleMmbtu ? (
              <KpiTile
                icon={<IconGauge size={18} />}
                color="cyan"
                label="Eligible MMBtu HHV"
                figure={eligibleMmbtu}
              />
            ) : null}
            {rfGallons ? (
              <KpiTile
                icon={<IconCoin size={18} />}
                color="orange"
                label="RF Gallons"
                figure={rfGallons}
              />
            ) : null}
            {d3Rins ? (
              <KpiTile
                icon={<IconCoin size={18} />}
                color="green"
                label="D3 RINs"
                figure={d3Rins}
              />
            ) : null}
          </SimpleGrid>
          <Text size="xs" c="dimmed">
            Figures are computed from source data. Approved corrections are disclosed in the RFS
            report and audit log but do not change these values.
          </Text>
        </Stack>
      )}

      <Card>
        <Group justify="space-between" mb="sm">
          <Title order={4}>Calculation reference</Title>
        </Group>
        <ScrollArea>
          <Table fz="sm" highlightOnHover withTableBorder withRowBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Quantity</Table.Th>
                <Table.Th>Formula</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>Daily Token MMBtu HHV</Table.Td>
                <Table.Td ff="monospace">(HUB MSCF × 1,000 × vw CH₄ × 1010 Btu/scf) ÷ 10⁶</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>Eligible MMBtu (HHV)</Table.Td>
                <Table.Td ff="monospace">min(Token MMBtu HHV, Utility MMBtu HHV)</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>RF Gallons</Table.Td>
                <Table.Td ff="monospace">Eligible MMBtu × 0.90 × 10⁶ ÷ 77,000</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>D3 RINs</Table.Td>
                <Table.Td ff="monospace">⌊RF Gallons × 1.0⌋</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Card>
    </Stack>
  );
}

function KpiTile({
  icon,
  color,
  label,
  figure,
}: {
  icon: React.ReactNode;
  color: string;
  label: string;
  figure: Figure;
}) {
  return (
    <Paper p="md" radius="md" withBorder>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={6}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
            {label}
          </Text>
          <UnitValue figure={figure as never} decimalPlaces={2} />
        </Stack>
        <ThemeIcon variant="light" color={color} radius="md" size={36}>
          {icon}
        </ThemeIcon>
      </Group>
    </Paper>
  );
}
